import { useEffect, useState } from 'react'
import { botApi, type BacktestResultData } from '../api'

export type BacktestKpisStatus = 'loading' | 'done' | 'error'

export interface BacktestKpis {
  data: BacktestResultData | null
  status: BacktestKpisStatus
}

// Results are immutable per btid — cache resolved values for the session so a
// chat full of historical badges fetches each run once (also dedupes
// StrictMode double-mounts and mobile-sheet remounts).
const resolved = new Map<string, BacktestResultData | null>()
const inFlight = new Map<string, Promise<BacktestResultData | null>>()

function fetchKpis(btid: string): Promise<BacktestResultData | null> {
  if (resolved.has(btid)) return Promise.resolve(resolved.get(btid) ?? null)
  let promise = inFlight.get(btid)
  if (!promise) {
    promise = botApi
      .getBacktestResults(btid)
      .then((data) => {
        resolved.set(btid, data)
        inFlight.delete(btid)
        return data
      })
      .catch((err) => {
        // Evict so a later remount retries instead of caching the failure.
        inFlight.delete(btid)
        throw err
      })
    inFlight.set(btid, promise)
  }
  return promise
}

export function useBacktestKpis(btid: string | undefined): BacktestKpis {
  const [state, setState] = useState<BacktestKpis>(() =>
    btid && resolved.has(btid)
      ? { data: resolved.get(btid) ?? null, status: 'done' }
      : { data: null, status: 'loading' },
  )

  // No synchronous setState here: a mount-time cache hit is seeded by the
  // useState initializer above, and a cached fetchKpis resolves in a
  // microtask — the effect only ever sets state asynchronously.
  useEffect(() => {
    if (!btid) return undefined
    let cancelled = false
    fetchKpis(btid)
      .then((data) => {
        if (!cancelled) setState({ data, status: 'done' })
      })
      .catch(() => {
        if (!cancelled) setState({ data: null, status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [btid])

  return state
}
